import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiBook, FiSearch, FiDownload, FiPrinter, FiChevronDown, FiChevronRight, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { TRAN_TYPE_ROUTES, TRAN_TYPE_LABELS } from '../../utils/navigationHelper';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { TRAN_TYPES } from '../../config/tranTypes';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// TRAN_TYPE_ROUTES + TRAN_TYPE_LABELS come from the shared navigationHelper (single
// source of truth) so the journal links AND labels EVERY tran type correctly — incl.
// IPI, CB, MIP, PRC, DEP, FAD, ADV, LON, PAY, FNF — with the right routes (the old
// local map sent NPI/NSI/NPR/NSR to the wrong pages and JV to a dead route).

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDate = (v) => fmtDate(v) || '-';

// Every accounting transaction type in the system (static, from config) so the
// Type filter always lists all possible types — not only the ones that happen
// to appear in the currently loaded data.
const ACCOUNTING_TRAN_TYPES = Object.values(TRAN_TYPES)
  .filter(t => t && t.affectsAccounting)
  .map(t => ({ code: t.code, name: t.name }))
  .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

const JournalReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('journal-report');
  const [entries, setEntries] = useState(() => savedMem?.entries ?? []);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(() => savedMem?.hasLoaded ?? false); // Tier 5: gated — no read until "Generate"
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [tranTypeFilter, setTranTypeFilter] = useState(() => savedMem?.tranTypeFilter ?? 'all');
  const [showUnbalancedOnly, setShowUnbalancedOnly] = useState(() => savedMem?.showUnbalancedOnly ?? false);
  const [expandedRows, setExpandedRows] = useState({});
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [parentTransactions, setParentTransactions] = useState(() => savedMem?.parentTransactions ?? {});

  // Navigate to transaction page
  const handleOpenTransaction = (tranTypeCode, transactionId, e) => {
    e.stopPropagation(); // Prevent row expand/collapse
    const route = TRAN_TYPE_ROUTES[tranTypeCode];
    if (route) {
      // Navigate with state to open the transaction in view mode
      navigate(route, { state: { viewTransactionId: transactionId } });
    }
  };

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Set default to active financial year
      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  useEffect(() => {
    loadFinancialYears();
  }, [loadFinancialYears]);

  // Load subTransactions (accounting entries) with status = "Posted"
  const loadEntries = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');

      // Build query conditions — include Draft OB entries (opening balances) alongside Posted
      const conditions = [where('status', 'in', ['Posted', 'Draft'])];

      if (selectedFYId) {
        conditions.push(where('financialYearId', '==', selectedFYId));
      }

      const q = query(subTransRef, ...conditions);
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);

      const allEntries = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => {
        // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
        if (t.status === 'Draft' && t.tranTypeCode !== 'OB') return false;
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Sort by docDate descending
      allEntries.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));

      setEntries(allEntries);
    } catch (error) {
      console.error('Error loading journal entries:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  // Load parent transactions to get job/employee info
  const loadParentTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const q = query(transRef, where('financialYearId', '==', selectedFYId));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const map = {};
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (activeBranch && activeBranch.id !== 'all' && data.branchId !== activeBranch.id) return;
        map[d.id] = {
          jobName: data.jobName || '',
          employeeName: data.employeeName || '',
          reference: data.reference || data.referenceNo || '',
          narration: data.narration || data.notes || ''
        };
      });
      setParentTransactions(map);
    } catch (error) {
      console.error('Error loading parent transactions:', error);
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  // Tier 5 (Firebase cost): NO auto-load. This report used to read the WHOLE
  // financial year's subTransactions (the biggest collection) PLUS all of that
  // year's transactions on mount. It now loads only when the user clicks
  // "Generate", so simply opening the page costs nothing.
  const generateReport = useCallback(() => {
    if (!selectedFYId) return;
    setHasLoaded(true);
    loadEntries();
    loadParentTransactions();
  }, [selectedFYId, loadEntries, loadParentTransactions]);

  // Remember this view (filters + loaded rows) once generated, so Back restores
  // it with 0 re-reads. Filters here are display-only over already-loaded data,
  // so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasLoaded) return;
    saveMemory({
      entries,
      parentTransactions,
      hasLoaded: true,
      searchQuery,
      dateFrom,
      dateTo,
      tranTypeFilter,
      showUnbalancedOnly,
      selectedFYId
    });
  }, [hasLoaded, entries, parentTransactions, searchQuery, dateFrom, dateTo,
      tranTypeFilter, showUnbalancedOnly, selectedFYId, saveMemory]);

  // Group entries by transactionId (docNumber)
  const groupedEntries = useMemo(() => {
    let filtered = entries;

    // Apply date filters
    if (dateFrom) {
      filtered = filtered.filter(e => (e.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(e => (e.docDate || '') <= dateTo);
    }

    // Apply transaction type filter
    if (tranTypeFilter !== 'all') {
      filtered = filtered.filter(e => e.tranTypeCode === tranTypeFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.docNumber?.toLowerCase().includes(q) ||
        e.description?.toLowerCase().includes(q) ||
        e.accountName?.toLowerCase().includes(q) ||
        e.accountCode?.toLowerCase().includes(q)
      );
    }

    // Group by transactionId
    const grouped = {};
    filtered.forEach(entry => {
      const key = entry.transactionId;
      if (!grouped[key]) {
        const parentTran = parentTransactions[entry.transactionId] || {};
        grouped[key] = {
          transactionId: entry.transactionId,
          tranTypeCode: entry.tranTypeCode,
          docNumber: entry.docNumber,
          docDate: entry.docDate,
          reference: parentTran.reference || parentTran.referenceNo || '',
          narration: parentTran.narration || parentTran.notes || '',
          jobName: parentTran.jobName || '',
          employeeName: parentTran.employeeName || '',
          lines: [],
          totalDebit: 0,
          totalCredit: 0
        };
      }
      grouped[key].lines.push(entry);
      grouped[key].totalDebit += entry.debitAmount || 0;
      grouped[key].totalCredit += entry.creditAmount || 0;
    });

    // Optionally keep only documents whose debits and credits don't match —
    // these are the broken/ghost entries the user wants to hunt down.
    let result = Object.values(grouped);
    if (showUnbalancedOnly) {
      result = result.filter(g => Math.abs((g.totalDebit || 0) - (g.totalCredit || 0)) > 0.01);
    }

    // Sort by date descending
    return result.sort((a, b) =>
      (b.docDate || '').localeCompare(a.docDate || '')
    );
  }, [entries, dateFrom, dateTo, tranTypeFilter, searchQuery, showUnbalancedOnly, parentTransactions]);

  // Summary
  const summary = useMemo(() => {
    const totalDebit = groupedEntries.reduce((s, e) => s + (e.totalDebit || 0), 0);
    const totalCredit = groupedEntries.reduce((s, e) => s + (e.totalCredit || 0), 0);
    return {
      totalEntries: groupedEntries.length,
      totalDebit,
      totalCredit
    };
  }, [groupedEntries]);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const allExpanded = {};
    groupedEntries.forEach(e => { allExpanded[e.transactionId] = true; });
    setExpandedRows(allExpanded);
  };

  const collapseAll = () => setExpandedRows({});

  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Journal Report\n';
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'All'} to ${dateTo || 'All'}\n\n`;
    csv += 'Doc Number,Date,Tran Type,Reference,Narration,Job,Employee,Account Code,Account Name,Description,Debit,Credit\n';

    groupedEntries.forEach(group => {
      group.lines.forEach((line, i) => {
        csv += `"${group.docNumber || ''}","${formatDate(group.docDate)}","${TRAN_TYPE_LABELS[group.tranTypeCode] || group.tranTypeCode || ''}","${(group.reference || '').replace(/"/g, '""')}","${(group.narration || '').replace(/"/g, '""')}","${group.jobName || ''}","${group.employeeName || ''}",`;
        csv += `"${line.accountCode || ''}","${(line.accountName || '').replace(/"/g, '""')}","${(line.description || '').replace(/"/g, '""')}",`;
        csv += `${line.debitAmount || 0},${line.creditAmount || 0}\n`;
      });
    });

    csv += `\nTotal Transactions:,${groupedEntries.length}\n`;
    csv += `Total Debit:,${summary.totalDebit}\n`;
    csv += `Total Credit:,${summary.totalCredit}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Journal_Report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Journal Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:8px;text-align:left;font-size:11px;text-transform:uppercase}
        td{padding:6px 8px;border-bottom:1px solid #ddd}
        .entry-row{background:#f9f9f9;font-weight:600}
        .line-row td{padding-left:30px;font-size:12px;color:#444}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .debit{color:#1565C0} .credit{color:#C62828}
        .totals{margin-top:20px;float:right;width:300px}
        .totals td{padding:5px 10px;font-weight:600}
        .totals tr:last-child td{border-top:2px solid #800020;font-size:14px}
        .tran-badge{font-size:11px;padding:2px 6px;border-radius:3px;background:#eee}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Journal Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        Date Range: ${dateFrom || 'All'} to ${dateTo || 'All'} |
        Transactions: ${groupedEntries.length}
      </div>
      <table>
        <thead><tr>
          <th>Doc #</th><th>Date</th><th>Type</th><th>Reference</th><th>Narration</th><th>Job</th><th>Employee</th><th>Account</th><th>Description</th>
          <th class="amount">Debit</th><th class="amount">Credit</th>
        </tr></thead>
        <tbody>
        ${groupedEntries.map(group => {
          let rows = `<tr class="entry-row">
            <td>${group.docNumber || ''}</td>
            <td>${formatDate(group.docDate)}</td>
            <td><span class="tran-badge">${TRAN_TYPE_LABELS[group.tranTypeCode] || group.tranTypeCode || '-'}</span></td>
            <td>${group.reference || '-'}</td>
            <td>${group.narration || '-'}</td>
            <td>${group.jobName || '-'}</td>
            <td>${group.employeeName || '-'}</td>
            <td></td>
            <td></td>
            <td class="amount debit">${formatAmount(group.totalDebit)}</td>
            <td class="amount credit">${formatAmount(group.totalCredit)}</td>
          </tr>`;
          group.lines.forEach(line => {
            rows += `<tr class="line-row">
              <td></td><td></td><td></td><td></td><td></td><td></td><td></td>
              <td>${line.accountCode || ''} - ${line.accountName || ''}</td>
              <td>${line.description || ''}</td>
              <td class="amount debit">${line.debitAmount > 0 ? formatAmount(line.debitAmount) : ''}</td>
              <td class="amount credit">${line.creditAmount > 0 ? formatAmount(line.creditAmount) : ''}</td>
            </tr>`;
          });
          return rows;
        }).join('')}
        </tbody>
      </table>
      <div class="totals"><table>
        <tr><td>Total Debit</td><td class="amount">${formatAmount(summary.totalDebit)}</td></tr>
        <tr><td>Total Credit</td><td class="amount">${formatAmount(summary.totalCredit)}</td></tr>
        <tr><td>Balance</td><td class="amount">${formatAmount(summary.totalDebit - summary.totalCredit)}</td></tr>
      </table></div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Journal Report" breadcrumb="Reports / Accounting Reports / Journal Report">
      {/* Summary Cards */}
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiBook /></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Transactions</SummaryLabel>
            <SummaryValue>{summary.totalEntries}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#1565C0"><span style={{ fontSize: 16, fontWeight: 700 }}>Dr</span></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Debit</SummaryLabel>
            <SummaryValue style={{ color: '#1565C0' }}>{formatAmount(summary.totalDebit)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#C62828"><span style={{ fontSize: 16, fontWeight: 700 }}>Cr</span></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Credit</SummaryLabel>
            <SummaryValue style={{ color: '#C62828' }}>{formatAmount(summary.totalCredit)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={Math.abs(summary.totalDebit - summary.totalCredit) < 0.01 ? colors.success.main : colors.error.main}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>=</span>
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Balance</SummaryLabel>
            <SummaryValue style={{ color: Math.abs(summary.totalDebit - summary.totalCredit) < 0.01 ? colors.success.main : colors.error.main }}>
              {Math.abs(summary.totalDebit - summary.totalCredit) < 0.01 ? 'Balanced' : formatAmount(summary.totalDebit - summary.totalCredit)}
            </SummaryValue>
          </SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <DateInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <DateInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Transaction Type</Label>
            <Select value={tranTypeFilter} onChange={e => setTranTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              {ACCOUNTING_TRAN_TYPES.map(t => (
                <option key={t.code} value={t.code}>{TRAN_TYPE_LABELS[t.code] || t.name}</option>
              ))}
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <SearchInputWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <Input
              type="text"
              placeholder="Search doc#, account, description..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchInputWrap>
          <CheckboxLabel
            $active={showUnbalancedOnly}
            title="Show only documents where total Debit does not equal total Credit"
          >
            <input
              type="checkbox"
              checked={showUnbalancedOnly}
              onChange={e => setShowUnbalancedOnly(e.target.checked)}
            />
            Show unbalanced only
          </CheckboxLabel>
          <ButtonGroup>
            <Button primary onClick={generateReport} disabled={!selectedFYId || loading}>
              <FiBook /> {loading ? 'Loading...' : 'Generate'}
            </Button>
            <Button onClick={expandAll} title="Expand All"><FiChevronDown /> Expand</Button>
            <Button onClick={collapseAll} title="Collapse All"><FiChevronRight /> Collapse</Button>
            <Button onClick={handleExport}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>Loading journal entries...</EmptyState>
          ) : !selectedFYId ? (
            <EmptyState>
              <FiCalendar size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Please select a financial year to view journal entries.</div>
            </EmptyState>
          ) : !hasLoaded ? (
            <EmptyState>
              <FiBook size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Select a financial year and click "Generate" to load journal entries.</div>
            </EmptyState>
          ) : groupedEntries.length === 0 ? (
            <EmptyState>
              <FiBook size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>{entries.length === 0 ? 'No posted journal entries yet. Post a transaction to generate entries.' : 'No entries match your filters.'}</div>
            </EmptyState>
          ) : (
            <Table>
              <thead>
                <Tr>
                  <Th style={{ width: 30 }}></Th>
                  <Th>Document #</Th>
                  <Th>Date</Th>
                  <Th>Transaction Type</Th>
                  <Th>Reference</Th>
                  <Th>Narration</Th>
                  <Th>Job</Th>
                  <Th>Employee</Th>
                  <Th>Lines</Th>
                  <Th align="right">Debit</Th>
                  <Th align="right">Credit</Th>
                </Tr>
              </thead>
              <tbody>
                {groupedEntries.map(group => (
                  <React.Fragment key={group.transactionId}>
                    <EntryRow onClick={() => toggleRow(group.transactionId)} $expanded={expandedRows[group.transactionId]}>
                      <Td style={{ textAlign: 'center', cursor: 'pointer', color: colors.text.secondary }}>
                        {expandedRows[group.transactionId] ? <FiChevronDown /> : <FiChevronRight />}
                      </Td>
                      <Td>
                        <DocNumber
                          onClick={(e) => handleOpenTransaction(group.tranTypeCode, group.transactionId, e)}
                          title="Click to open transaction"
                        >
                          {group.docNumber} <FiExternalLink size={12} />
                        </DocNumber>
                      </Td>
                      <Td>{formatDate(group.docDate)}</Td>
                      <Td><TranBadge $type={group.tranTypeCode}>{TRAN_TYPE_LABELS[group.tranTypeCode] || group.tranTypeCode || '-'}</TranBadge></Td>
                      <Td style={{ fontSize: 12 }}>{group.reference || '-'}</Td>
                      <Td style={{ fontSize: 12, color: colors.text.secondary, maxWidth: 220, whiteSpace: 'normal' }} title={group.narration || ''}>{group.narration || '-'}</Td>
                      <Td style={{ fontSize: 12, color: colors.text.secondary }}>{group.jobName || '-'}</Td>
                      <Td style={{ fontSize: 12, color: colors.text.secondary }}>{group.employeeName || '-'}</Td>
                      <Td>
                        {group.lines.length} entries
                        {Math.abs((group.totalDebit || 0) - (group.totalCredit || 0)) > 0.01 && (
                          <UnbalTag title="Debit ≠ Credit">
                            Off by {formatAmount(Math.abs(group.totalDebit - group.totalCredit))}
                          </UnbalTag>
                        )}
                      </Td>
                      <AmountTd align="right" $debit>{formatAmount(group.totalDebit)}</AmountTd>
                      <AmountTd align="right" $credit>{formatAmount(group.totalCredit)}</AmountTd>
                    </EntryRow>
                    {expandedRows[group.transactionId] && group.lines.map((line, li) => (
                      <LineRow key={`${group.transactionId}-${li}`}>
                        <Td></Td>
                        <Td colSpan={6}>
                          <AccountCode>{line.accountCode}</AccountCode>
                        </Td>
                        <Td colSpan={2}>
                          <AccountName $indent={line.creditAmount > 0}>{line.accountName}</AccountName>
                          {line.description && <LineDesc>{line.description}</LineDesc>}
                        </Td>
                        <AmountTd align="right" $debit>{line.debitAmount > 0 ? formatAmount(line.debitAmount) : ''}</AmountTd>
                        <AmountTd align="right" $credit>{line.creditAmount > 0 ? formatAmount(line.creditAmount) : ''}</AmountTd>
                      </LineRow>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <TotalRow>
                  <Td colSpan={9} style={{ textAlign: 'right', fontWeight: 700 }}>Grand Total</Td>
                  <AmountTd align="right" $debit style={{ fontWeight: 700, fontSize: 15 }}>{formatAmount(summary.totalDebit)}</AmountTd>
                  <AmountTd align="right" $credit style={{ fontWeight: 700, fontSize: 15 }}>{formatAmount(summary.totalCredit)}</AmountTd>
                </TotalRow>
              </tfoot>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius?.md || '6px'};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const SummaryInfo = styled.div``;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 1px solid ${props => props.$active ? colors.error.main : colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  background: ${props => props.$active ? colors.error.main + '10' : 'white'};
  color: ${props => props.$active ? colors.error.main : colors.text.primary};
  transition: all 0.2s;
  input { cursor: pointer; accent-color: ${colors.error.main}; }
`;

const UnbalTag = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: ${colors.error.main}15;
  color: ${colors.error.main};
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const EntryRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid ${colors.neutral[100]};
  background: ${props => props.$expanded ? colors.primary.main + '06' : 'transparent'};
  &:hover { background: ${colors.neutral[50]}; }
`;

const LineRow = styled.tr`
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[100]}; }
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};
  td { padding: 14px; }
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  color: ${props => props.$debit ? '#1565C0' : props.$credit ? '#C62828' : colors.text.primary};
  font-weight: 500;
`;

const DocNumber = styled.span`
  font-weight: 600;
  color: ${colors.primary.main};
  font-size: 13px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.6;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const TranBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    const t = props.$type;
    if (t === 'PI' || t === 'PR' || t === 'GRN' || t === 'PO') return '#E3F2FD';
    if (t === 'SI' || t === 'PS' || t === 'SR' || t === 'DC' || t === 'SO' || t === 'QT') return '#FFF3E0';
    if (t === 'CR' || t === 'BR') return '#E8F5E9';
    if (t === 'CP' || t === 'BP') return '#FFEBEE';
    if (t === 'JV' || t === 'OB') return '#F3E5F5';
    if (t === 'AS' || t === 'RS') return '#E0F7FA';
    return colors.neutral[100];
  }};
  color: ${props => {
    const t = props.$type;
    if (t === 'PI' || t === 'PR' || t === 'GRN' || t === 'PO') return '#1565C0';
    if (t === 'SI' || t === 'PS' || t === 'SR' || t === 'DC' || t === 'SO' || t === 'QT') return '#E65100';
    if (t === 'CR' || t === 'BR') return '#2E7D32';
    if (t === 'CP' || t === 'BP') return '#C62828';
    if (t === 'JV' || t === 'OB') return '#7B1FA2';
    if (t === 'AS' || t === 'RS') return '#00838F';
    return colors.text.secondary;
  }};
`;

const AccountCode = styled.span`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 11px;
  padding: 2px 6px;
  background: ${colors.neutral[100]};
  border-radius: 3px;
  color: ${colors.text.secondary};
`;

const AccountName = styled.span`
  font-size: 13px;
  color: ${colors.text.primary};
  ${props => props.$indent && 'padding-left: 20px;'}
`;

const LineDesc = styled.span`
  font-size: 11px;
  color: ${colors.text.secondary};
  margin-left: 8px;
  font-style: italic;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
  font-size: 14px;
`;

export default JournalReport;
